import type { V2 } from '../core/math';

/**
 * Lock-on feel, pure (lockFeel.test.ts): what keeps the soft lock from blinking and jumping, and the
 * melee lunge that closes the last bit of a gap. The weapon system applies these every step; the body's
 * damped aim turn is `aimTurnHeading` in src/input/footShaping.ts (the player controller owns the turn).
 */

/**
 * The lock's target went down (or a tapped target dropped): the gun stays off everybody this long (s)
 * before the soft lock looks for the next man — with FIRE held, the next round no longer goes into
 * whoever happened to stand beside him in the same frame.
 */
export const LOCK_REACQUIRE_DELAY = 0.3;

/**
 * A kept lock that just lost its line of sight (a lamp post, a wall corner the target walks past) stays
 * the lock this long (s): the brackets no longer blink off and on at every edge. A round fired meanwhile
 * still traces into whatever is in the way.
 */
export const LOCK_SIGHT_GRACE = 0.3;

/** Whether a kept lock counts as in sight: seen now, or last seen within the grace. */
export function seenWithGrace(seen: boolean, lastSeenAt: number, now: number, grace = LOCK_SIGHT_GRACE): boolean {
  return seen || now - lastSeenAt <= grace;
}

/** Most the lunge adds on top of the walk (m/s). */
export const LUNGE_MAX_SPEED = 4;
/** The lunge aims to put the target this deep inside reach (fraction of the reach), not at its very edge. */
export const LUNGE_REACH_FRACTION = 0.8;

/**
 * Melee lunge: during a swing's wind-up the body closes the gap to the man it goes at, so a blow started
 * a step too far still lands. The velocity (m/s) that brings the target's edge to LUNGE_REACH_FRACTION of
 * the reach by the time `timeLeft` (s, until the blow lands) is up, capped at LUNGE_MAX_SPEED; zero when
 * already in reach or no time is left. Add it to the walk and cap the total (`capSpeed`): never above the
 * server's FOOT_MAX_SPEED.
 */
export function lungeVelocity(from: V2, target: V2, targetR: number, reach: number, timeLeft: number): V2 {
  const dx = target.x - from.x;
  const dz = target.z - from.z;
  const d = Math.hypot(dx, dz);
  if (d < 1e-3 || timeLeft <= 1e-3) return { x: 0, z: 0 };
  const gap = d - targetR - reach * LUNGE_REACH_FRACTION;
  if (gap <= 0) return { x: 0, z: 0 };
  const speed = Math.min(LUNGE_MAX_SPEED, gap / timeLeft);
  return { x: (dx / d) * speed, z: (dz / d) * speed };
}

/** The velocity scaled down to `max` m/s if it is over it. */
export function capSpeed(vx: number, vz: number, max: number): V2 {
  const s = Math.hypot(vx, vz);
  if (s <= max || s < 1e-6) return { x: vx, z: vz };
  const k = max / s;
  return { x: vx * k, z: vz * k };
}

/** The lock brackets ease from the old target to the new one over this long (s); tracking one target has no lag. */
export const LOCK_SWITCH_EASE = 0.12;

/**
 * Where the brackets are drawn while they hand over from one target to another: `t` seconds into the
 * switch, from the spot they were at to where the new target is now (smoothstep). Past the ease they sit
 * on the target.
 */
export function bracketSwitchPos(from: { x: number; y: number }, to: { x: number; y: number }, t: number, ease = LOCK_SWITCH_EASE): { x: number; y: number } {
  if (t >= ease || ease <= 0) return { x: to.x, y: to.y };
  const u = Math.max(0, t / ease);
  const s = u * u * (3 - 2 * u);
  return { x: from.x + (to.x - from.x) * s, y: from.y + (to.y - from.y) * s };
}
